//#region IMPORT
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import MovieIcon from '@mui/icons-material/Movie';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import ApartmentIcon from '@mui/icons-material/Apartment';
import GroupIcon from '@mui/icons-material/Group';
import CategoryIcon from '@mui/icons-material/Category';
import { showSnackbar } from '../../../../../Reducer/SnackbarReducer';
import { Combo } from '../../../../../Components/MUIs';
import BackClickBottomSheet from '../../../../../Components/BottomSheet/BackClickBottomSheet';
//#endregion

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function M_TRA_BASIC_03(props) {
  const {
    ToggleDrawer,
    bottomSheetState,
    id,
    custInfo,
    videoFile,
    videoUrl,
    materialFile,
    previewUrl,
    selectedHeadquarters,
    selectedDepartments,
    selectedTeams,
    selectedParts,
    departmentData,
    videoInputRef,
    materialInputRef,
    dispatch,
    HandleWorkplaceChange,
    HandleVideoUpload,
    HandleSave,
    HandleRemoveVideo,
    HandleRemoveMaterial,
    HandleHeadquartersCheckboxChange,
    HandleTeamCheckboxChange,
    HandlePartCheckboxChange,
    HandleDepartmentCheckboxChange,
    HandleMaterialUpload,
    GetAvailableDepartments,
    GetFinalSelectedItems,
    GetAvailableTeams,
    GetAvailableParts,
    RenderPreview,
  } = props;

  return (
    <Box
      sx={{
        height: 'calc(100vh - 56px)',
        display: 'flex',
        flexDirection: 'column',
        px: 2,
        py: 2,
        gap: 3,
      }}
    >
      {/* 사업장 선택 섹션 */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 'bold',
            mb: 2,
            color: '#0D843F',
          }}
        >
          사업장 선택
        </Typography>
        <Combo
          name="사업장을 선택하세요"
          items={custInfo.custList}
          value={custInfo.custCd}
          onChange={HandleWorkplaceChange}
          wrapSx={{ width: '100%' }}
          disabled={id}
        />
      </Box>

      {/* 동영상 업로드 섹션 */}
      <Box>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 'bold',
            mb: 2,
            color: '#0D843F',
          }}
        >
          동영상 업로드
        </Typography>
        <Box
          sx={{
            border: '2px dashed #ccc',
            borderRadius: '8px',
            p: 3,
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            mb: 2,
          }}
        >
          {videoFile || videoUrl ? (
            // 동영상이 업로드된 경우 미리보기 표시
            <Box>
              <video
                controls
                style={{
                  width: '100%',
                  maxHeight: '200px',
                  borderRadius: '4px',
                  backgroundColor: '#000',
                  marginBottom: '16px',
                }}
                src={videoFile ? URL.createObjectURL(videoFile) : videoUrl}
              >
                브라우저가 비디오를 지원하지 않습니다.
              </video>

              <Button
                variant="outlined"
                size="small"
                component="label"
                sx={{
                  color: '#0D843F',
                  borderColor: '#0D843F',
                  '&:hover': {
                    borderColor: '#0D843F',
                    backgroundColor: '#E8F5E8',
                  },
                }}
              >
                동영상 변경
                <VisuallyHiddenInput
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  onChange={HandleVideoUpload}
                />
              </Button>
            </Box>
          ) : (
            // 동영상이 없는 경우 업로드 안내
            <Box>
              <MovieIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography sx={{ fontSize: '16px', mb: 1, color: '#666' }}>
                동영상을 업로드하세요
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#999', mb: 2 }}>
                MP4, AVI, MOV 형식 지원
              </Typography>
              <Button
                variant="contained"
                component="label"
                sx={{
                  backgroundColor: '#0D843F',
                  '&:hover': { backgroundColor: '#0a6b32' },
                }}
              >
                동영상 선택
                <VisuallyHiddenInput
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  onChange={HandleVideoUpload}
                />
              </Button>
            </Box>
          )}
        </Box>

        {/* 업로드된 동영상 목록 (칩 형태) */}
        {(videoFile || videoUrl) && (
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold', mb: 1 }}>
              업로드된 동영상
            </Typography>
            <Chip
              label={
                videoFile
                  ? `${videoFile.name} (${(
                      videoFile.size /
                      1024 /
                      1024
                    ).toFixed(2)} MB)`
                  : videoUrl
                  ? videoUrl.split('/').pop()
                  : '동영상 파일'
              }
              onDelete={() => HandleRemoveVideo()}
              sx={{
                m: 0.5,
                backgroundColor: '#E8F5E8',
                color: '#0D843F',
                '& .MuiChip-deleteIcon': {
                  color: '#0D843F',
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* 자료 업로드 섹션 */}
      <Box>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 'bold',
            mb: 2,
            color: '#0D843F',
          }}
        >
          자료 업로드
        </Typography>
        <Box
          sx={{
            border: '2px dashed #ccc',
            borderRadius: '8px',
            p: 3,
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            mb: 2,
          }}
        >
          {materialFile || previewUrl ? (
            // 교육자료가 업로드된 경우 미리보기 표시
            <Box>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}
              >
                {RenderPreview ? RenderPreview() : '미리보기 영역'}
              </Box>

              <Button
                variant="outlined"
                size="small"
                component="label"
                sx={{
                  color: '#0D843F',
                  borderColor: '#0D843F',
                  '&:hover': {
                    borderColor: '#0D843F',
                    backgroundColor: '#E8F5E8',
                  },
                }}
              >
                자료 변경
                <VisuallyHiddenInput
                  ref={materialInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.hwp"
                  onChange={HandleMaterialUpload}
                />
              </Button>
            </Box>
          ) : (
            // 교육자료가 없는 경우 업로드 안내
            <Box>
              <DescriptionIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography sx={{ fontSize: '16px', mb: 1, color: '#666' }}>
                자료를 업로드하세요
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#999', mb: 2 }}>
                PDF, HWP, DOC, DOCX, XLS, XLSX, PPT, PPTX 형식 지원
              </Typography>
              <Button
                variant="contained"
                component="label"
                sx={{
                  backgroundColor: '#0D843F',
                  '&:hover': { backgroundColor: '#0a6b32' },
                }}
              >
                자료 선택
                <VisuallyHiddenInput
                  ref={materialInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.hwp"
                  onChange={HandleMaterialUpload}
                />
              </Button>
            </Box>
          )}
        </Box>

        {/* 업로드된 자료 목록 (칩 형태) */}
        {(materialFile || previewUrl) && (
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold', mb: 1 }}>
              업로드된 자료
            </Typography>
            <Chip
              label={
                materialFile
                  ? `${materialFile.name} (${(
                      materialFile.size /
                      1024 /
                      1024
                    ).toFixed(2)} MB)`
                  : previewUrl
                  ? previewUrl.split('/').pop()
                  : '자료 파일'
              }
              onDelete={() => HandleRemoveMaterial()}
              sx={{
                m: 0.5,
                backgroundColor: '#E8F5E8',
                color: '#0D843F',
                '& .MuiChip-deleteIcon': {
                  color: '#0D843F',
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* 부서 선택 섹션 */}
      <Box>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 'bold',
            mb: 2,
            color: '#0D843F',
          }}
        >
          부서 선택
        </Typography>

        {/* 1단계: 본부 아코디언 */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <BusinessIcon sx={{ color: '#42A5F5', mr: 1, fontSize: '20px' }} />
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#42A5F5',
              }}
            >
              본부
            </Typography>
          </Box>
          <Accordion
            sx={{
              '&:before': { display: 'none' },
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderRadius: '8px !important',
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: '#E3F2FD',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: '#42A5F5',
                  fontSize: '16px',
                }}
              >
                본부 목록
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: '#FAFAFA', p: 2 }}>
              {departmentData.headquarters.map((hq) => (
                <FormControlLabel
                  key={hq.id}
                  control={
                    <Checkbox
                      checked={selectedHeadquarters.includes(hq.id)}
                      onChange={(e) =>
                        HandleHeadquartersCheckboxChange(
                          hq.id,
                          e.target.checked
                        )
                      }
                      disabled={id}
                      sx={{ color: '#42A5F5' }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        color: '#42A5F5',
                        fontSize: '15px',
                      }}
                    >
                      {hq.name}
                    </Typography>
                  }
                />
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* 2단계: 부서 아코디언 */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ApartmentIcon sx={{ color: '#7B1FA2', mr: 1, fontSize: '20px' }} />
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#7B1FA2',
              }}
            >
              부서
            </Typography>
          </Box>
          <Accordion
            disabled={selectedHeadquarters.length === 0}
            sx={{
              '&:before': { display: 'none' },
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderRadius: '8px !important',
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: '#F3E5F5',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: '#7B1FA2',
                  fontSize: '16px',
                }}
              >
                부서 목록
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: '#FAFAFA', p: 2 }}>
              {GetAvailableDepartments().map((dept) => (
                <FormControlLabel
                  key={dept.id}
                  control={
                    <Checkbox
                      checked={selectedDepartments.includes(dept.id)}
                      onChange={(e) =>
                        HandleDepartmentCheckboxChange(
                          dept.id,
                          e.target.checked
                        )
                      }
                      disabled={id}
                      sx={{ color: '#7B1FA2' }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        color: '#7B1FA2',
                        fontSize: '15px',
                      }}
                    >
                      {dept.name}
                    </Typography>
                  }
                />
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* 3단계: 팀 아코디언 */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <GroupIcon sx={{ color: '#0D843F', mr: 1, fontSize: '20px' }} />
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#0D843F',
              }}
            >
              팀
            </Typography>
          </Box>
          <Accordion
            disabled={selectedDepartments.length === 0}
            sx={{
              '&:before': { display: 'none' },
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderRadius: '8px !important',
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: '#E8F5E8',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: '#0D843F',
                  fontSize: '16px',
                }}
              >
                팀 목록
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: '#FAFAFA', p: 2 }}>
              {GetAvailableTeams().map((team) => (
                <FormControlLabel
                  key={team.id}
                  control={
                    <Checkbox
                      checked={selectedTeams.includes(team.id)}
                      onChange={(e) =>
                        HandleTeamCheckboxChange(team.id, e.target.checked)
                      }
                      disabled={id}
                      sx={{ color: '#0D843F' }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        color: '#0D843F',
                        fontSize: '15px',
                      }}
                    >
                      {team.name}
                    </Typography>
                  }
                />
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* 4단계: 파트 아코디언 */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CategoryIcon sx={{ color: '#F57C00', mr: 1, fontSize: '20px' }} />
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#F57C00',
              }}
            >
              파트
            </Typography>
          </Box>
          <Accordion
            disabled={selectedTeams.length === 0}
            sx={{
              '&:before': { display: 'none' },
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderRadius: '8px !important',
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: '#FFF3E0',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: '#F57C00',
                  fontSize: '16px',
                }}
              >
                파트 목록
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: '#FAFAFA', p: 2 }}>
              {GetAvailableParts().map((part) => (
                <FormControlLabel
                  key={part.id}
                  control={
                    <Checkbox
                      checked={selectedParts.includes(part.id)}
                      onChange={(e) =>
                        HandlePartCheckboxChange(part.id, e.target.checked)
                      }
                      disabled={id}
                      sx={{ color: '#F57C00' }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        color: '#F57C00',
                        fontSize: '15px',
                      }}
                    >
                      {part.name}
                    </Typography>
                  }
                />
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* 등록 대상 목록 */}
        <Box sx={{ mt: 3 }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 'bold',
              mb: 2,
              color: '#0D843F',
            }}
          >
            등록 대상 목록
          </Typography>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              p: 2,
              backgroundColor: '#f9f9f9',
              minHeight: '60px',
            }}
          >
            {GetFinalSelectedItems().length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {GetFinalSelectedItems().map((item) => (
                  <Chip
                    key={item.id}
                    label={item.name}
                    sx={{
                      backgroundColor: item.backgroundColor,
                      color: item.color,
                      fontWeight: 'bold',
                      fontSize: '14px',
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography
                sx={{
                  color: '#999',
                  fontSize: '14px',
                  textAlign: 'center',
                  py: 2,
                }}
              >
                선택된 부서가 없습니다.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* 저장 버튼 */}
      <Box sx={{ mt: 'auto', pb: 2 }}>
        <Button
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: '#57B84D',
            '&:hover': { backgroundColor: '#4A9B42' },
            py: 1.5,
            height: 36,
          }}
          onClick={async () => {
            if (!custInfo.custCd) {
              dispatch(
                showSnackbar({
                  message: '사업장을 선택해주세요.',
                  type: 'warning',
                })
              );
              return;
            }

            if (!videoFile && !videoUrl) {
              dispatch(
                showSnackbar({
                  message: '동영상을 업로드해주세요.',
                  type: 'warning',
                })
              );
              return;
            }

            if (!materialFile && !previewUrl) {
              dispatch(
                showSnackbar({
                  message: '교육 자료를 업로드해주세요.',
                  type: 'warning',
                })
              );
              return;
            }

            if (selectedHeadquarters.length === 0) {
              dispatch(
                showSnackbar({
                  message: '부서를 선택해주세요.',
                  type: 'warning',
                })
              );
              return;
            }

            if (HandleSave) {
              const success = await HandleSave();
              if (success) {
                // 성공 시 폼 초기화는 부모 컴포넌트에서 처리
              }
            }
          }}
        >
          자료 등록
        </Button>
      </Box>

      {/* 바텀시트 */}
      {bottomSheetState.bottom && bottomSheetState.type === 'backClickTra' && (
        <BackClickBottomSheet
          open={true}
          ToggleDrawer={ToggleDrawer}
          route={'/M_TrainingPage'}
        />
      )}
    </Box>
  );
}

export default M_TRA_BASIC_03;
